import * as d3 from 'd3'
import { max as maxL, min as minL } from 'lodash'

export const getXMinimum = model => {
  const barsXs = (model?.bars ?? []).map(b => b.x)
  const xpos = model.x ?? [0]
  const w = barsXs.length > 1 ? barsXs[1] - barsXs[0] : 0
  const lefts = barsXs.map(v => v - w / 2)
  return d3.min([...lefts, ...xpos]) ?? 0
}

export const getXMaximum = model => {
  const barsXs = (model?.bars ?? []).map(b => b.x)
  const xpos = model.x ?? [0]
  const w = barsXs.length > 1 ? barsXs[1] - barsXs[0] : 0
  const rights = barsXs.map(v => v + w / 2)
  return d3.max([...rights, ...xpos]) ?? 0
}

export const getXExtrem = model => {
  const minX = getXMinimum(model)
  const maxX = getXMaximum(model)
  return [minX, maxX + maxX * 0.1]
}

export const getVWExtrem = model => {
  const plots = model?.plots || null

  if (!plots || !Object.keys(plots).some(k => k.slice(0, 2) === 'VW')) return [0, 0]

  const keys = [
    'VW_TooCheap_curve_plot',
    'VW_Cheap_curve_plot',
    'VW_NotExpensive_curve_plot',
    'VW_NotCheap_curve_plot',
    'VW_Expensive_curve_plot',
    'VW_TooExpensive_curve_plot'
  ]

  let max = 0
  let min = 0

  keys.forEach(k => {
    const [low, high] = d3.extent(plots?.[k] || [])
    if (Number.isFinite(high)) max = Math.max(max, Number(high))
    if (Number.isFinite(low)) min = Math.min(min, Number(low))
  })

  return [max, min]
}

export const mergeVWPoints = ({ xpos, gpoints }) => {
  // TODO: This is the same function as mergePoints.
  // Replace it after removing crunch with Y-axis limitation by backend.
  const data = []
  gpoints.forEach((value, key) => {
    data.push({ x: xpos[key], y: value })
  })
  return data
}

export const getVWGraphs = model => {
  const xpos = model.x || [0]
  const plots = model.plots || null

  if (!plots || !Object.keys(plots).some(k => k.slice(0, 2) === 'VW')) {
    return {}
  }

  return {
    TooCheap: {
      text: 'Too Cheap',
      stroke: { width: 4, dash: 0 },
      data: mergeVWPoints({ xpos, gpoints: plots['VW_TooCheap_curve_plot'] || [] })
    },
    Cheap: {
      text: 'Cheap',
      stroke: { width: 2, dash: 0 },
      data: mergeVWPoints({ xpos, gpoints: plots['VW_Cheap_curve_plot'] || [] })
    },
    NotExpensive: {
      text: 'Not Expensive',
      stroke: { width: 2, dash: '2% 0.5%' },
      data: mergeVWPoints({ xpos, gpoints: plots['VW_NotExpensive_curve_plot'] || [] })
    },
    NotCheap: {
      text: 'Not Cheap',
      stroke: { width: 2, dash: '2% 0.5%' },
      data: mergeVWPoints({ xpos, gpoints: plots['VW_NotCheap_curve_plot'] || [] })
    },
    Expensive: {
      text: 'Expensive',
      stroke: { width: 2, dash: 0 },
      data: mergeVWPoints({ xpos, gpoints: plots['VW_Expensive_curve_plot'] || [] })
    },
    TooExpensive: {
      text: 'Too Expensive',
      stroke: { width: 4, dash: 0 },
      data: mergeVWPoints({ xpos, gpoints: plots['VW_TooExpensive_curve_plot'] || [] })
    }
  }
}

export const getVWArea = model => {
  const points = model?.points || null

  let result = { data: [0, 0], text: '' }
  const hasVW = points && points.some(d => d['_Name']?.slice(0, 2) === 'vw')

  if (hasVW) {
    const vw_toocheap = points.find(d => d['_Name'] === 'vw_toocheap')
    const vw_tooexp = points.find(d => d['_Name'] === 'vw_tooexp')
    if (vw_toocheap && vw_tooexp) {
      result = {
        data: [vw_toocheap['_Price'], vw_tooexp['_Price']],
        text: `${vw_toocheap['UNIT.PRICE'].replace('/person', '')} - ${vw_tooexp['UNIT.PRICE'].replace('/person', '')}`
      }
    }
  }
  return result
}

export const getVWPoints = model => {
  const points = model?.points || null

  let p = {}
  const hasVW = points && points.some(d => d['_Name']?.slice(0, 2) === 'vw')
  if (hasVW) {
    const f = name => points.find(d => d['_Name'] === name)
    const vw_toocheap = f('vw_toocheap')
    const vw_tooexp = f('vw_tooexp')
    const vw_optimal = f('vw_optimal')
    const vw_indiff = f('vw_indiff')

    if (vw_toocheap && vw_tooexp && vw_optimal && vw_indiff) {
      p = {
        toocheap: {
          x: vw_toocheap['_Price'],
          y: vw_toocheap['_VW'],
          text: '',
          label: { text: vw_toocheap['PRICE.POINTS'], prop: vw_toocheap['UNIT.PRICE'] }
        },
        tooexp: {
          x: vw_tooexp['_Price'],
          y: vw_tooexp['_VW'],
          text: '',
          label: { text: vw_tooexp['PRICE.POINTS'], prop: vw_tooexp['UNIT.PRICE'] }
        },
        optimal: {
          x: vw_optimal['_Price'],
          y: vw_optimal['_VW'],
          text: vw_optimal['UNIT.PRICE']?.replace('/person', '') || '',
          label: { text: vw_optimal['UNIT.PRICE']?.replace('/person', '') || '', prop: vw_optimal['UNIT.PRICE'] }
        },
        indiff: {
          x: vw_indiff['_Price'],
          y: vw_indiff['_VW'],
          text: 'Indifference point',
          label: { text: vw_indiff['PRICE.POINTS'], prop: vw_indiff['UNIT.PRICE'] }
        }
      }
    }
  }
  return p
}

export const getYMinimum = model => {
  const bars = model?.bars?.map(v => v.y) || [0]
  const graph1YValue = model?.graph1 || [0]
  const graph2YValue = model?.graph2 || [0]

  const gBext = d3.extent(bars)
  if (graph1YValue) {
    const g1ext = d3.extent(graph1YValue)
    if (graph2YValue) {
      const g2ext = d3.extent(graph2YValue)
      return minL([gBext[0], g1ext[0], g2ext[0]])
    }
    return minL([gBext[0], g1ext[0]])
  }
  return gBext[0]
}

export const getYMaximum = model => {
  const bars = model?.bars?.map(v => v.y) || [0]
  const graph1YValue = model?.graph1 || [0]
  const graph2YValue = model?.graph2 || [0]

  const gBext = d3.extent(bars)
  if (graph1YValue) {
    const g1ext = d3.extent(graph1YValue)
    if (graph2YValue) {
      const g2ext = d3.extent(graph2YValue)
      return maxL([gBext[1], g1ext[1], g2ext[1]])
    }
    return maxL([gBext[1], g1ext[1]])
  }
  return gBext[1]
}

export const getYExtrem = model => {
  const bars = model?.bars?.map(v => v.y) || [0]
  const graph1YValue = model?.graph1 || [0]
  const graph2YValue = model?.graph2 || [0]
  const yLimit = model?.yLimit

  const maximized = model?.maximized ?? null

  const gBext = d3.extent(bars)

  let min = null
  let max = null

  if (graph1YValue) {
    const g1ext = d3.extent(graph1YValue)
    if (graph2YValue) {
      const g2ext = d3.extent(graph2YValue)
      min = minL([gBext[0], g1ext[0], g2ext[0]])
      max = maxL([gBext[1], g1ext[1], g2ext[1]])
    } else {
      min = minL([gBext[0], g1ext[0]])
      max = maxL([gBext[1], g1ext[1]])
    }
  } else {
    min = gBext[0]
    max = gBext[1]
  }

  if (Math.abs(Number(min)) < Math.abs(Number(max)) / 2) min = -Number(max)
  if (yLimit != null) min = parseInt(yLimit, 10)

  if (maximized != null) {
    return [Number(max) + (Number(max) * maximized) / 100, Number(min)]
  }
  return [Number(max), Number(min)]
}

export const valueFormatter = (model, opts) => {
  const { xTemplate, yTemplate, xPrefix, yPrefix } = model
  const { axis, toFixed = false } = opts
  let value = opts?.value || 0

  const template = (axis === 'x' && xTemplate) || (axis === 'y' && yTemplate) || null
  const prefix = (axis === 'x' && xPrefix) || yPrefix || ''
  let postfix = ''

  const sign = Math.sign(value)

  value = Math.abs(value)

  if (
    (value < 1 && value % 0.1 === 0) ||
    (value >= 1 && value < 2.5) ||
    (value >= 2.5 && value < 5) ||
    (value >= 5 && value < 10) ||
    (value >= 10 && value < 25) ||
    (value >= 25 && value < 50) ||
    (value >= 50 && value < 100) ||
    (value >= 100 && value < 250) ||
    (value >= 250 && value < 500) ||
    (value >= 500 && value < 1000)
  ) {
    postfix = ''
  } else if (
    (value >= 1000 && value < 2500) ||
    (value >= 2500 && value < 5000) ||
    (value >= 5000 && value < 10000) ||
    (value >= 10000 && value < 25000) ||
    (value >= 25000 && value < 50000) ||
    (value >= 50000 && value < 100000) ||
    (value >= 100000 && value < 250000) ||
    (value >= 250000 && value < 500000) ||
    (value >= 500000 && value < 1000000)
  ) {
    value = value / 1000
    postfix = 'k'
  } else if (value >= 1000000) {
    value = value / 1000000
    if (value < 1000) {
      postfix = 'M'
    } else if (value < 1000000) {
      value = value / 1000
      postfix = 'B'
    } else {
      value = value / 1000000
      postfix = 'T'
    }
  }

  if (toFixed) value = Number(value).toFixed(2)

  if (!toFixed && value === 0) return ''

  const txt = template?.replace(`%${axis}`, `${value}${postfix}`) || `${prefix}${value}${postfix}`
  return (sign < 0 ? '-' : '') + txt
}

export const axisGridFormater = (model, opts) => {
  const { percentage, gridOpacity } = model
  const { axis, value } = opts

  if (axis === 'y') {
    if (percentage) {
      const v = parseFloat(value) * 100
      return v % 20 === 0 ? gridOpacity : 0
    }
    return value % 1000 === 0 ? gridOpacity : 0
  } else if (axis === 'x') {
    return valueFormatter(model, {
      axis: 'x',
      value
    })
      ? gridOpacity
      : 0
  }
  return 0
}

export const getPoints = model => {
  const points = []
  const p = model.p
  if (!p) return points

  const custom = Boolean(model.points)
  const pointsPlot = custom ? p.filter(d => model.points.includes(d['_Name'])) : p

  pointsPlot.forEach(point => {
    const px = parseFloat(point['_Price'])
    let py = parseFloat(point['_Revenue'])

    if (custom) {
      let key
      switch (point['_Name']) {
        case 'vw_optimal':
        case 'clients_price':
        case 'break_even':
          key = model?.graph2 ? '_Profit' : '_Revenue'
          break
        case 'max_prof':
        case 'max_freq':
          key = '_Profit'
          break
        case 'max_rev':
        default:
          key = '_Revenue'
      }
      py = parseFloat(point[key])
    }

    // a point with no figure for the measure it is plotted on has nowhere to be drawn
    if (!Number.isFinite(py)) return

    points.push({
      n: point['_Name'],
      x: px,
      y: py,
      i: point['_Index'] - 1
    })
  })

  return points
}

export const getPoint = (model, value, key) => {
  const point = (model.p || []).find(d => d['_Name'] === value)
  const ttsVal = model.tooltipsVal

  // a point with no figure for the asked measure has nowhere to be labelled
  if (point && Number.isFinite(parseFloat(point[key]))) {
    let prop = point['REVENUE']
    if (value === 'max_prof') prop = point['P&L']
    if (['max_prob', 'max_freq'].includes(value)) prop = ttsVal

    return {
      x: parseFloat(point['_Price']),
      y: parseFloat(point[key]),
      i: point['_Index'] - 1,
      text: point['PRICE.POINTS'],
      value: point['UNIT.PRICE'],
      prop
    }
  }

  return { x: -100, y: -100, text: '', value: '', prop: '' }
}

export const formatterXaxis = (model, opts) => {
  const { value, xMax, toFixed = false, force = false } = opts

  if (value < xMax || force) {
    return valueFormatter(model, {
      axis: 'x',
      value: parseFloat(value),
      toFixed
    })
  }
  return ''
}

export const formatterYaxis = (model, opts) => {
  const { value, yMax, toFixed = false, force = false } = opts

  if (value < yMax || force) {
    if (model?.percentage === true) {
      const v = parseFloat(value) * 100

      if (v % 10 === 0) {
        return `${v}%`
      }
    } else {
      return valueFormatter(model, {
        axis: 'y',
        value: parseFloat(value),
        toFixed
      })
    }
  }
  return ''
}

export const mergePoints = ({ model, xpos, gpoints }) => {
  const data = []
  const minY = getYExtrem(model)[1] // note: same as CoffeeScript (upper bound)
  gpoints.forEach((value, key) => {
    if (value < minY) return // TODO: remove after backend calc
    data.push({ x: xpos[key], y: value })
  })
  return data
}

export const getGraph1 = model => {
  const xpos = model?.x || [0]
  const gpoints = model?.graph1
  return gpoints ? mergePoints({ model, xpos, gpoints }) : []
}

export const getGraph2 = model => {
  const xpos = model?.x || [0]
  const gpoints = model?.graph2

  return gpoints ? mergePoints({ model, xpos, gpoints }) : []
}

export const getYbyX = (model, x, g = 1) => {
  const graph = g === 1 ? getGraph1(model) : getGraph2(model)
  const point = graph.find(p => p.x > x)
  return point ? point.y : undefined
}

export const getFreqPeriod = model => {
  const freqPeriod = model?.freqPeriod
  const freqArray = ['', '', '/day', '/week', '/month', '/quarter', '/year']
  return freqPeriod ? freqArray[freqPeriod] : ''
}

export const mergeAreaPoints = ({ xpos, gpoints, area }) => {
  const data = []
  gpoints.forEach((value, key) => {
    const x = xpos[key]
    if (x >= area.left_x && x <= area.right_x) {
      data.push({ x, y: value })
    }
  })
  return data
}

export const getBars = ({ model }) => {
  const { bars = [], scale = 0.8 } = model

  if (!Array.isArray(bars) || bars.length <= 1) return []

  const w = scale * (bars[1].x - bars[0].x)

  return bars.map(b => [
    { x: Number(b.x - w / 2), y: Number(b.y) },
    { x: Number(b.x + w / 2), y: Number(b.y) }
  ])
}

export const getAreas = (model = {}) => {
  const { x = [], graph1 = [], areas, area } = model ?? {}
  const list = areas?.[area]

  if (!Array.isArray(list) || list?.length === 0) return []

  return list.map(_area =>
    mergeAreaPoints({
      xpos: x,
      gpoints: graph1,
      area: _area
    })
  )
}
